import React from 'react';
import * as Inovx from '@shared';
import FacilityActions from './../actions/FacilityActions';
import AssemblyLibraryActions from './../actions/AssemblyLibraryActions';
import FacilityStore from './../stores/FacilityStore';
import FacilityPreferences from './FacilityPreferences';
import SettingActions from '../actions/SettingActions';
import SchemaActions from '../actions/SchemaActions';
import ViewerActions from '../actions/ViewerActions';
import ViewerStore from '../stores/ViewerStore';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import AssetSetsActions from '../actions/AssetSetsActions';
import GraphicSourceActions from '../actions/GraphicSourceActions';
import OntologyActions from '../actions/OntologyActions';
import ManageApp from './ManageApp';
import ViewerApp from './ViewerApp';
import FacilityExplorerApp from './FacilityExplorerApp';
import UrlSyncManager from '../Utilities/UrlSyncManager';
import TaxonomyActions from '../actions/TaxonomyActions';
var UrlUtilities = Inovx.Shared.Common.UrlUtilities;
var PreferencesActions = Inovx.Shared.Preferences.Actions;

declare var InovxLoader: any;

interface FacilityProps {
    mode: 'manage' | 'explore';
}

interface FacilityState {
    selectedFacility?: IFacilityDetail;
    isPanoViewerVisible?: boolean;
    isPointViewerVisible?: boolean;
    is3DViewerVisible?: boolean;
}

function getFacilityState(): FacilityState {
    return {
        selectedFacility: FacilityStore.getSelectedFacility(),
        isPanoViewerVisible: ViewerStore.isPanoViewerVisible(),
        isPointViewerVisible: ViewerStore.isOpenPointCloud(),
        is3DViewerVisible: ViewerStore.is3DViewerVisible()
    }
}

class FacilityApp extends Inovx.Shared.ReactExt.BaseAppComponent<FacilityProps, FacilityState> {
    private queryParams: QueryParams;

    constructor(props: FacilityProps, context: any) {
        super('facility', props, context);
        
        this.state = getFacilityState();       
        this.queryParams = FacilityActions.getParamsFromCurrentURL();

        // document.getElementById("ihdr-share-email").remove();
        // document.getElementById("ihdr-share-export-glb").remove();
        // document.getElementById("ihdr-share-export-obj").remove();
        // document.getElementById("ihdr-print").remove();
       
        FacilityActions.updateContextHelp(this.queryParams);

        InovxLoader.addLanguage('viewer');
    }

    shouldComponentUpdate(nextProps: FacilityProps, nextState: FacilityState) {
        if (this.queryParams.facilityId) {
            return true;
        } else if (this.queryParams.viewId || this.queryParams.kvId) {
            if (ViewerStore.isPanoViewerVisible() !== this.state.isPanoViewerVisible ||
                ViewerStore.is3DViewerVisible() !== this.state.is3DViewerVisible ||
                ViewerStore.isOpenPointCloud() !== this.state.isPointViewerVisible) {
                return true;
            } else {
                return false;
            }
        }
        return true;
    }

    private _is3DMode(mode: QueryParams['mode'] = this.queryParams.mode) {
        return mode === '3D' || mode === '3DRange';
    }

    private _isKnowledgeLibraryCardMode() {
        return this.queryParams.mode === 'klibcard' || !!this.queryParams.cltagId;
    }

    private async _openRangeViewForAsset(assetId: string) {
        const facility = FacilityStore.getSelectedFacility();
        const assetTag = facility?.AssetTags?.find(tag => tag.ID === assetId);
        const ontology = FacilityStore.getSelectedOntology() || FacilityStore.getRootOntologyPart();

        if (!ontology) {
            return false;
        }

        let focusType: 'asset' | 'tag';
        let rangePosition: IVector3D;

        if (assetTag?.Target3D) {
            focusType = 'tag';
            rangePosition = assetTag.Target3D;
        } else {
            const extent = await ViewerActions.getHierarchyItemExtents(assetId);
            if (!extent) {
                return false;
            }

            const min = {
                X: extent.MinExtents.X / 1000,
                Z: extent.MinExtents.Y / 1000,
                Y: extent.MinExtents.Z / 1000,
            };

            const max = {
                X: extent.MaxExtents.X / 1000,
                Z: extent.MaxExtents.Y / 1000,
                Y: extent.MaxExtents.Z / 1000,
            };

            focusType = 'asset';
            rangePosition = {
                X: min.X + (max.X - min.X) / 2,
                Y: min.Y + (max.Y - min.Y) / 2,
                Z: min.Z + (max.Z - min.Z) / 2,
            };
        }

        OntologyActions.selectOntology(ontology);
        await FacilityActions.openLODViewer(ontology.ID, ["Point Cloud Data", "CAD Data", "Mesh Data"], 30.0, rangePosition, 0.001, false);

        const current = ViewerStore.getContentLayers();
        const next = { ...current };

        next.pointCloud = focusType === 'tag';
        next.assets = focusType === 'asset';
        next.assetTags = focusType === 'tag';
        next.cad = false;
        next.positions360 = false;
        next.scanPositions = false;
        next.assemblies = false;
        next.mesh = false;

        ViewerActions.setContentLayers(next);
        return true;
    }

    protected async _initialize(isFromPopState: boolean = false) {
        try {
            PreferencesActions.registerCreatePreferencesComponent(this._createPreferencesComponent, InovxLoader.t('facility:menu.facilityExplorer'))
            
            if (!this.queryParams.facilityId && !this.queryParams.viewId && !this.queryParams.kvId && !this.queryParams.kbId && !this.queryParams.assetSetId) {
                if (this.props.mode === 'explore') {
                    window.location.href = "/home";
                    return;
                } else if (this.props.mode === 'manage' && isFromPopState) {
                    FacilityActions.resetFacilityDetails();
                    return;
                }
            }

            const initFuncList: Promise<any>[] = [
                SettingActions.getSettings()
            ];

            if (this.props.mode === 'manage') {
                initFuncList.push(
                    FacilityActions.getAllFacilities(),
                    SchemaActions.getAllSchemas(),
                    TaxonomyActions.getAllTaxonomy()
                );
            }
            else {
                initFuncList.push(
                    AssemblyLibraryActions.getAssemblyLibrariesWithTemplates()
                );
            }
            
            await Promise.all(initFuncList);

            if (this.queryParams.facilityId && !this.queryParams.kvId && !this.queryParams.viewId && !this.queryParams.rangeViewId && (!this.queryParams.kbId || this._isKnowledgeLibraryCardMode()) && !this.queryParams.assetSetId) {
                await this._initFacilityDetails(this.queryParams.facilityId, this.queryParams.ontologyId);
                if (this._isKnowledgeLibraryCardMode()) {
                    FacilityActions.setKnowledgeLibraryCardUrlState({
                        isCardView: true,
                        classificationTagId: this.queryParams.cltagId,
                        knowledgeBookId: this.queryParams.kbId
                    });
                    await FacilityActions.openKnowledgeLibrary();
                }
                else if (this._is3DMode()) {
                    const ontology = FacilityStore.getSelectedOntology();
                    const openedRangeView = this.queryParams.mode === '3DRange' && this.queryParams.assetId
                        ? await this._openRangeViewForAsset(this.queryParams.assetId)
                        : false;

                    if (!openedRangeView) {
                        await FacilityActions.openLODViewer(ontology.ID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
                    }
                }
                else if (this.queryParams.mode === 'klib') {
                    FacilityActions.setKnowledgeLibraryCardUrlState({ isCardView: false, classificationTagId: undefined, knowledgeBookId: undefined });
                    await FacilityActions.openKnowledgeLibrary();
                }
                else if (this.queryParams.mode === 'media') {
                    FacilityActions.openMediaGallery();
                }
            }
            
            if (this.queryParams.kvId) {
                const kv = await KnowledgeBookActions.getKnowledgeViewData(this.queryParams.kvId);
                if (kv) {
                    const kb = await KnowledgeBookActions.getKnowledgeBook(kv.KnowledgeBookID);
                    if (kb) {
                        this.queryParams.facilityId = kb.FacilityID;
                        this.queryParams.ontologyId = kb.OntologyID;
                        await this._initFacilityDetails(this.queryParams.facilityId, this.queryParams.ontologyId);
                    
                        await KnowledgeBookActions.openKnowledgeBook(kb);
                        KnowledgeBookActions.pinUnpinKBPopup(true); // does this belong here?
                        await FacilityActions.openKnowledgeViewInViewer(kv);
                    }
                }
            }
            else if (this.queryParams.assetSetId) {
                const assetSet = await AssetSetsActions.getAssetSetDetail(this.queryParams.assetSetId);
                if (assetSet) {
                    const kb = await KnowledgeBookActions.getKnowledgeBook(assetSet.KnowledgeBookID);
                    if (kb) {
                        this.queryParams.facilityId = kb.FacilityID;
                        this.queryParams.ontologyId = kb.OntologyID;
                        this.queryParams.mode = '3D';

                        await this._initFacilityDetails(this.queryParams.facilityId, this.queryParams.ontologyId);
                        await KnowledgeBookActions.openKnowledgeBook(kb);
                        KnowledgeBookActions.pinUnpinKBPopup(true);

                        if (kb.OntologyID) {
                            await FacilityActions.openLODViewer(kb.OntologyID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
                            await ViewerActions.changeKnowledgeBookViewingMode('manageAssets');
                            await ViewerActions.selectAssetSet(assetSet);
                        }
                    }
                }
            }
            else if (this.queryParams.kbId) {
                const kb = await KnowledgeBookActions.getKnowledgeBook(this.queryParams.kbId);                
                if (kb) {
                    this.queryParams.facilityId = kb.FacilityID;
                    this.queryParams.ontologyId = kb.OntologyID;
                    await this._initFacilityDetails(this.queryParams.facilityId, this.queryParams.ontologyId);

                    const kbData = await KnowledgeBookActions.openKnowledgeBook(kb);
                    KnowledgeBookActions.pinUnpinKBPopup(true); // does this belong here?

                    if (!this.queryParams.mode) {
                        // If no mode is specified, default to opening the first knowledge view
                        if (kbData.KnowledgeViews && kbData.KnowledgeViews.length > 0) {
                            const kv = kbData.KnowledgeViews?.[0];                       
                            await FacilityActions.openKnowledgeViewInViewer(kv);
                        }
                    } else if (this._is3DMode()) {
                        // If mode is 3D, open the LOD viewer without selecting an asset set
                        if (kb.OntologyID) {
                            const openedRangeView = this.queryParams.mode === '3DRange' && this.queryParams.assetId
                                ? await this._openRangeViewForAsset(this.queryParams.assetId)
                                : false;

                            if (!openedRangeView) {
                                await FacilityActions.openLODViewer(kb.OntologyID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
                            }
                        }
                    } else if (this.queryParams.mode === '3DSet') {
                        // If mode is 3DSet, open the LOD viewer and set the viewing mode to manageAssets to show the asset sets
                        if (kb.OntologyID) {
                            await FacilityActions.openLODViewer(kb.OntologyID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
                            await ViewerActions.changeKnowledgeBookViewingMode('manageAssets');
                        }
                    } else if (this.queryParams.mode === '360') {
                        // If mode is 360, open the pano viewer with the pano view in the knowledge book
                        if (this.queryParams.viewId) {
                            const view = await FacilityActions.getView360Data(this.queryParams.viewId);
                            const facility = FacilityStore.getSelectedFacility();
                            const viewDetail = facility?.Views?.find(item => item.ID === view?.ID);
                            if (viewDetail) {
                                ViewerActions.showPanoViewerImage(viewDetail);
                            }
                        } else {
                            // If no specific view is specified for 360 mode, open the LOD viewer instead
                            if (kb.OntologyID) {
                                await FacilityActions.openLODViewer(kb.OntologyID, ["Point Cloud Data", "CAD Data", "Mesh Data"]);
                            }
                        }
                    }
                }
            }
            else {
                if (this.queryParams.assetId && !this._is3DMode() && !(this.queryParams.viewId || this.queryParams.rangeViewId)) {
                    const assetTag = this.state.selectedFacility?.AssetTags?.find(tag => tag.ID === this.queryParams.assetId);
                    if (assetTag) {
                        this.queryParams.viewId = assetTag.View360ID;
                    }
                }

                if (this.queryParams.viewId || this.queryParams.rangeViewId) {
                    const view = await FacilityActions.getView360Data(this.queryParams.viewId || this.queryParams.rangeViewId);
                    if (view) {
                        this.queryParams.facilityId = view.FacilityID;
                        await this._initFacilityDetails(this.queryParams.facilityId, this.queryParams.ontologyId);

                        const viewDetail = this.state.selectedFacility.Views.find(item => item.ID === view.ID);
                        if (viewDetail) {
                            ViewerActions.showPanoViewerImage(viewDetail);

                            // If a rangeViewId is specified, and mode is 3D, open the LOD viewer at the range view position
                            if (this.queryParams.rangeViewId && this._is3DMode()) {

                                ViewerActions.setCameraProperties(Math.PI * -1, Math.PI / 2, 5);
                                const ontology = FacilityStore.getRootOntologyPart();

                                const current = ViewerStore.getContentLayers();
                                const next = { ...current };

                                next.pointCloud = true;
                                next.assets = false;
                                next.assetTags = false;
                                next.cad = false;
                                next.positions360 = true;
                                next.scanPositions = true;
                                next.assemblies = false;
                                next.mesh = false;

                                // leave other flags (scanPositions, assetTags, assemblies) as-is
                                OntologyActions.selectOntology(ontology);
                                ViewerActions.setContentLayers(next);
                                await FacilityActions.openLODViewer(ontology.ID, ["Point Cloud Data", "CAD Data", "Mesh Data"], 30.0, viewDetail.Position, .001, false, viewDetail);
                            }
                        }
                    }
                }
            }

            if (this.queryParams.assetId) {
                const assetTag = this.state.selectedFacility.AssetTags?.find(tag => tag.ID === this.queryParams.assetId);
                if (assetTag) {
                    if (this._is3DMode()) {
                        ViewerActions.setLayerVisible('assetTags');
                    }
                    setTimeout(async () => {
                        ViewerActions.selectAnnotation(assetTag.ID);
                        ViewerActions.focusAnnotation(Object.assign({}, assetTag));
                    }, 1000);
                }
                else {
                    if (this._is3DMode()) {
                        setTimeout(async () => {
                            await ViewerActions.selectHierarchyItem(this.queryParams.assetId, false);
                            ViewerActions.focusHierarchyItem(this.queryParams.assetId);
                        }, 1000);
                    }
                }
            }

            UrlSyncManager.start();

            this._appLoaded = true;
            this.setState({});
        }
        catch (error) {
            this._handlePermissionError(error);
        }
    }
    private async _initFacilityDetails(facilityId: string, ontologyId: string) {
        const facility = await FacilityActions.openFacilityDetail(facilityId);
        await SchemaActions.getAssetClassesForUpdate(facility.SchemaID);
        await ViewerActions.createAssemblyInstances(facility.Assemblies);

        if (ontologyId) {
            let initialOntologyItem: IOntologyPart = this.state.selectedFacility.OntologyParts?.find(part => part.ID === ontologyId) ?? FacilityStore.getRootOntologyPart();
            OntologyActions.selectOntology(initialOntologyItem);
        }

        if (this.props.mode === "manage") {
            OntologyActions.beginEditOntology();
        }
    }
   
    private _updateAppTitle = (title: string) => {
        const headerBarTitleElement = document.getElementById("nav-app-title");
        if (headerBarTitleElement) {
            setTimeout(() => {
                headerBarTitleElement.textContent = title;
            }, 10);//added delay so that the title is updated after pano is unloaded
        }
    }
    
    private _onChange = () => {
        this.setState(getFacilityState());
    }

    UNSAFE_componentWillMount() {
        this.componentWillMountBase();
    }

    componentDidMount() {
        FacilityStore.addChangeListener(this._onChange);
        ViewerStore.addChangeListener(this._onChange);
        
        let that = this;
        window.onpopstate = function (event) {
            UrlSyncManager.stop();
            that.queryParams = FacilityActions.getParamsFromCurrentURL();
            that._initialize(true);

            FacilityActions.updateContextHelp(that.queryParams);
        };

    }
    componentDidUpdate() {
        const { selectedFacility, isPanoViewerVisible, is3DViewerVisible } = this.state;
        if (selectedFacility && !isPanoViewerVisible && !is3DViewerVisible) {
            this._updateAppTitle(this.state.selectedFacility?.Name)
        } else if (!selectedFacility) {
            this._updateAppTitle('')
        }
    }
    componentWillUnmount() {
        FacilityStore.removeChangeListener(this._onChange);
        ViewerStore.removeChangeListener(this._onChange);
        PreferencesActions.registerCreatePreferencesComponent(null, "");
        window.removeEventListener("resize", () => {           
        })
        UrlSyncManager.stop();
    }
    
    private _createPreferencesComponent = (props: any): any => {
        return React.createElement(FacilityPreferences, props);
    }

    public render() {
        const viewerVisible = this.state.isPanoViewerVisible || this.state.is3DViewerVisible;
        const facilitySelected = !!this.state.selectedFacility;
        return (
            <Inovx.Shared.Core.ReactLoader appReady={this.isAppLoaded()} errorStatus={this._errorStatus} className="facility-app-wrapper">
                {this.props.mode === 'manage' ? <ManageApp />
                    : viewerVisible ? <ViewerApp />
                        : facilitySelected ? <FacilityExplorerApp />
                            : null
                }
            </Inovx.Shared.Core.ReactLoader>
        );
    }
}

export default FacilityApp;
